import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getGreeting } from '../utils/formatters';
import { DATE_FILTERS } from '../utils/constants';

export function CommandBar({
  user, logoUrl, brandName, dateRange, startDate, endDate,
  isCustomDateOpen, unreadCount,
  onDateRangeChange, onCustomDateChange, onToggleCustomDate, onNotificationToggle,
}) {
  const greeting = getGreeting();
  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Left: Logo + Greeting */}
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
              {brandName?.[0] || 'G'}
            </div>
          )}
          <span className="text-sm font-medium text-gray-700 truncate">
            {greeting}, <span className="font-semibold text-gray-900">{firstName}</span>
          </span>
        </div>

        {/* Center: Date pills */}
        <div className="hidden md:flex items-center">
          <div className="pill-tab-container">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => onDateRangeChange(f.value)}
                className={`pill-tab ${dateRange === f.value ? 'pill-tab-active' : ''}`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => onToggleCustomDate(!isCustomDateOpen)}
              className={`pill-tab ${dateRange === 'custom' ? 'pill-tab-active' : ''}`}
            >
              <Calendar size={14} className="inline mr-1" />
              Custom
            </button>
          </div>
        </div>

        {/* Right: Notification bell + brand */}
        <div className="flex items-center gap-3">
          <span className="hidden lg:inline text-xs text-gray-400 font-medium">{brandName}</span>
          <button onClick={onNotificationToggle} className="notification-bell relative p-2 rounded-xl hover:bg-gray-50 transition-colors">
            <Bell size={18} className="text-gray-500" />
            {unreadCount > 0 && (
              <span className="notification-badge-pulse absolute -top-0.5 -right-0.5 w-4.5 h-4.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[18px] h-[18px]">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile date selector */}
      <div className="md:hidden mt-3 overflow-x-auto">
        <div className="pill-tab-container inline-flex">
          {DATE_FILTERS.slice(0, 4).map((f) => (
            <button
              key={f.value}
              onClick={() => onDateRangeChange(f.value)}
              className={`pill-tab text-xs ${dateRange === f.value ? 'pill-tab-active' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date picker dropdown */}
      <AnimatePresence>
        {isCustomDateOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 mt-2 p-4 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 flex gap-4"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Start</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  if (date && endDate) onCustomDateChange(date, endDate);
                  else if (date) onCustomDateChange(date, date);
                }}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                inline
                calendarClassName="!border-0 !shadow-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block">End</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  if (date && startDate) onCustomDateChange(startDate, date);
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                inline
                calendarClassName="!border-0 !shadow-none"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
